/**
 * Station alerts (the station workspaces plan, step 7; N3: the alert list is the Alarms page with a "Mine" filter): the
 * alerts the background loop raised from the stations' levels. On the Alarms page, above the hand-made alarms, Mine
 * (the stations the person belongs to; every one for an org admin) is on by default for a station member. On a
 * station's page, its own alerts, where its people acknowledge them (the alert email sends them there). Recently
 * cleared ones show on request. A vendor overlay, or an org without the foundry pack, sees no section.
 *
 * With none open, the section says what is true of the org's sources (lib/api/live-feed; the 2026-10-24 page sweep, H1
 * and the #1641 review, I1): gateways that sent nothing in 30 days can raise nothing, and a plant whose data all comes
 * from files raises no alert (history never alarms). Where the engine can fire (a live gateway, hand entry) the words
 * stay "No open alert"; while the state is unknown the section also says a plant fed by files gets no alerts.
 */
import { useState, type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { Card } from "@/lib/ui/card";
import {
  stationAlertsHidden,
  useAckStationAlert,
  useStationAlertsQuery,
  type StationAlert,
  type StationAlertAckKind,
  type StationAlertList,
} from "@/lib/api/station-alerts";
import { usePlantTimeQuery } from "@/lib/api/plant-time";
import { noAlarmsWords, useAlarmFeedState, type AlarmFeedState } from "@/lib/api/live-feed";
import { useMyStationQuery } from "@/lib/api/stations";
import { ackBody, ackErrorWords, ackProblem, alertAckable } from "@/lib/plant/station-alerts";
import { stationZone } from "@/routes/twin/station-format";
import { AlertCard } from "./AlertCard";

export interface StationAlertsViewProps {
  state: "loading" | "failed" | "ready";
  list?: StationAlertList;
  /** One station's own alerts (its page): no Mine filter. */
  atStation?: boolean;
  mine: boolean;
  showCleared: boolean;
  onMine: (on: boolean) => void;
  onShowCleared: (on: boolean) => void;
  renderAlert: (a: StationAlert) => ReactNode;
  /** What can raise an alarm at the org (lib/api/live-feed); undefined while unknown. */
  feed?: AlarmFeedState;
}

export function StationAlertsView(p: StationAlertsViewProps) {
  const items = p.list?.items ?? [];
  const mine = p.mine && !p.atStation;
  // Gateways gone quiet, or data from files alone: an empty list says why nothing is raised.
  const quiet = noAlarmsWords(p.feed);
  return (
    <Card className="flex flex-col gap-3 p-4" aria-label={p.atStation ? "This station's alerts" : "Station alerts"}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold text-on-surface">
          {p.atStation ? "Alerts" : "Station alerts"}{" "}
          {p.list && (
            <span className="font-normal text-on-surface-muted">
              ({mine ? `${p.list.mine_open} open at your stations` : `${p.list.open} open`})
            </span>
          )}
        </h2>
        <div className="flex flex-wrap items-center gap-3 text-xs text-on-surface">
          {!p.atStation && (
            <label className="inline-flex items-center gap-1.5">
              <input type="checkbox" checked={p.mine} onChange={(e) => p.onMine(e.target.checked)} />
              Mine
            </label>
          )}
          <label className="inline-flex items-center gap-1.5">
            <input type="checkbox" checked={p.showCleared} onChange={(e) => p.onShowCleared(e.target.checked)} />
            Cleared in the last week
          </label>
        </div>
      </div>
      <p className="text-xs text-on-surface-muted">
        Raised when a live value is outside the plant&apos;s window or past a stop limit.
        {(p.feed === undefined || p.feed === "none") && " A plant that sends its data as files gets no alerts."}
      </p>
      {p.state === "loading" ? (
        <p role="status" className="flex items-center gap-2 text-sm text-on-surface-muted">
          <Loader2 aria-hidden className="size-4 animate-spin" /> Reading the alerts
        </p>
      ) : p.state === "failed" ? (
        <p role="alert" className="text-sm text-danger">
          The alerts could not be read. They are tried again every minute; reload the page to try now.
        </p>
      ) : items.length === 0 ? (
        <p className="text-sm text-on-surface-muted">
          {quiet
            ? `${quiet.title}. ${quiet.description}`
            : p.atStation
              ? "No open alert at this station."
              : mine
                ? "No open alert at your stations."
                : "No open alert."}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">{items.map((a) => p.renderAlert(a))}</ul>
      )}
    </Card>
  );
}

/** One alert with its own acknowledgement state. */
function AlertCardState({ alert, zone }: { alert: StationAlert; zone?: string }) {
  const ack = useAckStationAlert();
  const [draft, setDraft] = useState<{ kind: StationAlertAckKind | ""; detail: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  return (
    <AlertCard
      alert={alert}
      zone={zone}
      ack={alertAckable(alert) ? draft : null}
      saving={ack.isPending}
      error={error}
      onAck={() => {
        setError(null);
        setDraft({ kind: "", detail: "" });
      }}
      onKind={(kind) => setDraft((d) => ({ kind, detail: d?.detail ?? "" }))}
      onDetail={(detail) => setDraft((d) => ({ kind: d?.kind ?? "", detail }))}
      onCancel={() => {
        setError(null);
        setDraft(null);
      }}
      onSave={() => {
        if (!draft || !draft.kind || ackProblem(draft.kind, draft.detail)) return;
        setError(null);
        ack.mutate(
          { id: alert.id, body: ackBody(draft.kind, draft.detail) },
          { onSuccess: () => setDraft(null), onError: (err) => setError(ackErrorWords(err)) },
        );
      }}
    />
  );
}

/**
 * The org's station alerts, or one station's (stationId: its page), read every minute. Hidden quietly for a caller they
 * are refused to.
 */
export function StationAlertsSection({ stationId }: { stationId?: string }) {
  const my = useMyStationQuery();
  const member = (my.data?.member_of.length ?? 0) > 0;
  const zone = stationZone(usePlantTimeQuery().data);
  // Mine starts on for a member of a station; the person's choice holds after that. A station's page has no Mine.
  const [mineChoice, setMineChoice] = useState<boolean | null>(null);
  const mine = stationId ? false : (mineChoice ?? member);
  const [showCleared, setShowCleared] = useState(false);
  const alerts = useStationAlertsQuery({ state: showCleared ? "all" : "open", mine, stationId });
  const feed = useAlarmFeedState();
  // Not read here at all (not the live platform), or refused for good: no section to show.
  if ((alerts.isPending && alerts.fetchStatus === "idle") || stationAlertsHidden(alerts.error)) return null;
  return (
    <StationAlertsView
      state={alerts.data ? "ready" : alerts.isError ? "failed" : "loading"}
      list={alerts.data}
      atStation={Boolean(stationId)}
      mine={mine}
      showCleared={showCleared}
      onMine={setMineChoice}
      onShowCleared={setShowCleared}
      renderAlert={(a) => <AlertCardState key={a.id} alert={a} zone={zone} />}
      feed={feed}
    />
  );
}
